import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CdkDropList, CdkDropListGroup, CdkDrag, CdkDragDrop, moveItemInArray, transferArrayItem, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { OpenSquashService } from '../shared/services/open-squash-service';
import { Match, MatchExtended, MatchState } from '../shared/model/match';
import { TournamentCategory } from '../shared/tournament-category';
import { ControlMatch, PlayerMatchStats, CATEGORY_COLORS } from './models/control-state';
import { ControlMatchCardComponent } from './control-match-card/control-match-card';
import { AssignCourtDialogComponent } from './assign-court-dialog/assign-court-dialog';
import { ShowQrDialogComponent } from './show-qr-dialog/show-qr-dialog';
import { SetOrderDialogComponent } from './set-order-dialog/set-order-dialog';
import {
  computePlayerStats,
  sortByPriority,
  splitByState,
  PARK_BOTTOM_THRESHOLD,
} from '../shared/util/match-priority.util';

@Component({
  selector: 'app-tournament-control',
  imports: [CommonModule, CdkDropListGroup, CdkDropList, CdkDrag, CdkDragHandle, ControlMatchCardComponent],
  templateUrl: './tournament-control-component.html',
  styleUrl: './tournament-control-component.scss',
})
export class TournamentControlComponent implements OnInit, OnDestroy {
  private service = inject(OpenSquashService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);

  esAdmin$!: Observable<boolean>;
  categories: TournamentCategory[] = [];
  selectedCategories: Set<string> = new Set();
  selectedGroup: string | null = null;
  availableGroups: string[] = [];

  matchesEnCancha: ControlMatch[] = [];
  matchesSiguientes: ControlMatch[] = [];
  matchesEnEspera: ControlMatch[] = [];
  matchesFinalizados: ControlMatch[] = [];
  showFinalizados = false;

  playerStats = new Map<string, PlayerMatchStats>();
  totalCourts = 4;
  freeCourts = 0;

  recentlyMovedId: string | null = null;

  private matchesSub!: Subscription;
  private tournamentSub!: Subscription;
  private controlOrderSub!: Subscription;
  private currentManualOrder: Record<string, number> = {};

  // Estado global de los partidos pendientes (sin filtrar). Se usa como
  // referencia para el drag/drop con "slot preservation".
  private globalPendientesSorted: ControlMatch[] = [];

  // IDs que estaban en cancha el ciclo anterior — para detectar
  // transiciones EN_CANCHA/JUGANDO → PROGRAMADO (reseteo).
  private previousInCourtIds: Set<string> = new Set();

  // Si el orden manual todavía no se ha cargado de Firestore, evitamos
  // detectar "reset" hasta tener el snapshot inicial (de lo contrario,
  // al cargar la página los EN_CANCHA actuales se interpretarían como
  // resets falsos al cabo de un ciclo).
  private hasInitialOrder = false;
  private recentlyMovedTimer: any;

  ngOnInit(): void {
    this.esAdmin$ = this.service.esAdmin$;
    this.totalCourts = this.loadCourtsConfig();

    this.tournamentSub = this.service.tournament$.subscribe(tournament => {
      this.categories = tournament.categories.sort(
        (a, b) => a.categoryOrder - b.categoryOrder
      );
    });

    this.matchesSub = this.service.getMatchesExtended().subscribe(matches => {
      this.processMatches(matches);
    });

    this.controlOrderSub = this.service.getControlOrder$().subscribe(order => {
      this.currentManualOrder = order || {};
      this.hasInitialOrder = true;
      this.reprocessCurrentMatches();
    });
  }

  ngOnDestroy(): void {
    this.matchesSub?.unsubscribe();
    this.tournamentSub?.unsubscribe();
    this.controlOrderSub?.unsubscribe();
    if (this.recentlyMovedTimer) clearTimeout(this.recentlyMovedTimer);
  }

  // --- Filtros de categoría ---

  toggleCategory(category: string): void {
    if (this.selectedCategories.has(category)) {
      this.selectedCategories.clear();
    } else {
      this.selectedCategories.clear();
      this.selectedCategories.add(category);
    }
    this.selectedGroup = null;
    this.reprocessCurrentMatches();
  }

  clearFilters(): void {
    this.selectedCategories.clear();
    this.selectedGroup = null;
    this.reprocessCurrentMatches();
  }

  isCategorySelected(category: string): boolean {
    return this.selectedCategories.has(category);
  }

  get hasFilters(): boolean {
    return this.selectedCategories.size > 0;
  }

  // --- Filtros de grupo (disponibles cuando hay una sola categoría seleccionada) ---

  selectGroup(group: string | null): void {
    this.selectedGroup = group;
    this.reprocessCurrentMatches();
  }

  isGroupSelected(group: string | null): boolean {
    return this.selectedGroup === group;
  }

  get showGroupFilters(): boolean {
    return this.selectedCategories.size === 1 && this.availableGroups.length > 1;
  }

  groupLabel(group: string): string {
    return group === 'finales' ? 'Finales' : group;
  }

  private computeAvailableGroups(matches: MatchExtended[]): void {
    if (this.selectedCategories.size !== 1) {
      this.availableGroups = [];
      return;
    }
    const cat = this.selectedCategories.values().next().value;
    const groups = new Set<string>();
    for (const m of matches) {
      if (m.category === cat && m.groupName) {
        groups.add(m.groupName);
      }
    }
    // 'finales' al final, el resto alfabético
    this.availableGroups = Array.from(groups).sort((a, b) => {
      if (a === 'finales') return 1;
      if (b === 'finales') return -1;
      return a.localeCompare(b);
    });
    if (this.selectedGroup && !this.availableGroups.includes(this.selectedGroup)) {
      this.selectedGroup = null;
    }
  }

  getCategoryColor(category: string): string {
    return CATEGORY_COLORS[category] || '#9E9E9E';
  }

  // --- Configuración de canchas ---

  increaseCourts(): void {
    this.totalCourts++;
    this.saveCourtsConfig();
    this.updateFreeCourts();
  }

  decreaseCourts(): void {
    if (this.totalCourts > 1) {
      this.totalCourts--;
      this.saveCourtsConfig();
      this.updateFreeCourts();
    }
  }

  // --- Drag & Drop ---

  onDropSiguientes(event: CdkDragDrop<ControlMatch[]>): void {
    this.applyDragDrop(event);
  }

  onDropEspera(event: CdkDragDrop<ControlMatch[]>): void {
    this.applyDragDrop(event);
  }

  /**
   * Tras un drag/drop dentro o entre las listas visibles (filtradas),
   * recomputa el orden global aplicando "slot preservation": los partidos
   * no visibles (filtrados) conservan su posición global exacta, y los
   * visibles rotan dentro de los slots que ocupaban.
   */
  private applyDragDrop(event: CdkDragDrop<ControlMatch[]>): void {
    const dragged = event.item.data as ControlMatch;

    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex
      );
    }

    // Lista visible (filtrada) en su nuevo orden = siguientes + espera
    const visibleNew = [...this.matchesSiguientes, ...this.matchesEnEspera];

    // Construir el nuevo orden global aplicando slot preservation
    const visibleIdSet = new Set(visibleNew.map(m => m.id!).filter(Boolean));
    const newGlobalIds: string[] = [];
    let visibleIdx = 0;
    for (const m of this.globalPendientesSorted) {
      if (!m.id) continue;
      if (visibleIdSet.has(m.id)) {
        newGlobalIds.push(visibleNew[visibleIdx].id!);
        visibleIdx++;
      } else {
        newGlobalIds.push(m.id);
      }
    }

    // Construir el manualOrder preservando el estado "parked" de los no-arrastrados
    const newOrder: Record<string, number> = {};
    let nonParkedCounter = 1;
    let parkedCounter = PARK_BOTTOM_THRESHOLD + 1;
    for (const id of newGlobalIds) {
      const wasParked = (this.currentManualOrder[id] ?? -1) >= PARK_BOTTOM_THRESHOLD;
      const isDragged = id === dragged?.id;
      if (wasParked && !isDragged) {
        newOrder[id] = parkedCounter++;
      } else {
        newOrder[id] = nonParkedCounter++;
      }
    }

    this.service.saveControlOrder(newOrder);
    this.markRecentlyMoved(dragged?.id);
  }

  private markRecentlyMoved(id: string | undefined): void {
    if (!id) return;
    this.recentlyMovedId = id;
    if (this.recentlyMovedTimer) clearTimeout(this.recentlyMovedTimer);
    this.recentlyMovedTimer = setTimeout(() => {
      this.recentlyMovedId = null;
    }, 2500);
  }

  // --- Acciones ---

  onStartMatch(match: ControlMatch): void {
    this.dialog.open(AssignCourtDialogComponent, {
      data: {
        match,
        totalCourts: this.totalCourts,
        occupiedCourts: this.matchesEnCancha.map(m => m.court),
      }
    }).afterClosed().subscribe(result => {
      if (result) {
        this.snackBar.open('Partido iniciado correctamente', 'Cerrar', { duration: 3000 });
      }
    });
  }

  onShowQr(match: ControlMatch): void {
    this.dialog.open(ShowQrDialogComponent, { data: { match } });
  }

  onSetOrder(match: ControlMatch): void {
    const currentPosition = match.globalPosition || 1;
    const maxPosition = this.globalPendientesSorted.length;
    if (maxPosition <= 0) return;

    this.dialog.open(SetOrderDialogComponent, {
      data: { match, currentPosition, maxPosition },
    }).afterClosed().subscribe((target: number | null) => {
      if (target == null || target === currentPosition) return;
      this.applySetOrder(match, target);
      this.markRecentlyMoved(match.id);
      this.snackBar.open(`Partido movido a la posición #${target}`, 'Cerrar', { duration: 2500 });
    });
  }

  /**
   * Aplica una nueva posición global a un partido. El resto de partidos
   * se desplaza una posición para hacer espacio, preservando el estado
   * "parked" de los no involucrados.
   */
  private applySetOrder(match: ControlMatch, targetPosition: number): void {
    const ids = this.globalPendientesSorted.map(m => m.id!).filter(Boolean) as string[];
    const fromIdx = ids.indexOf(match.id!);
    if (fromIdx < 0) return;

    // 1-indexed → 0-indexed
    const toIdx = Math.max(0, Math.min(ids.length - 1, targetPosition - 1));

    // Mover en el array global
    const [moved] = ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, moved);

    // Construir el manualOrder preservando "parked" de los no arrastrados
    const newOrder: Record<string, number> = {};
    let nonParkedCounter = 1;
    let parkedCounter = PARK_BOTTOM_THRESHOLD + 1;
    for (const id of ids) {
      const wasParked = (this.currentManualOrder[id] ?? -1) >= PARK_BOTTOM_THRESHOLD;
      const isDragged = id === match.id;
      if (wasParked && !isDragged) {
        newOrder[id] = parkedCounter++;
      } else {
        newOrder[id] = nonParkedCounter++;
      }
    }

    this.service.saveControlOrder(newOrder);
  }

  resetOrder(): void {
    if (!confirm('¿Restablecer el orden por defecto? Se eliminará cualquier orden manual asignado.')) return;
    this.service.saveControlOrder({});
    this.snackBar.open('Orden restablecido al automático', 'Cerrar', { duration: 2500 });
  }

  /**
   * Reinicia TODOS los partidos del torneo: pone marcadores en cero, vacía
   * `games` y `gamesDetailed` (los events de arbitraje) y devuelve el estado
   * a PROGRAMADO. Acción destructiva — requiere doble confirmación.
   */
  resetAllMatches(): void {
    if (!confirm('¿Reiniciar TODOS los partidos del torneo? Se borrarán los marcadores, los juegos detallados y los eventos de arbitraje de TODOS los partidos.')) return;
    if (!confirm('Confirma una segunda vez: esta acción es IRREVERSIBLE.')) return;

    const matches = this.lastMatches;
    if (!matches || matches.length === 0) {
      this.snackBar.open('No hay partidos para reiniciar', 'Cerrar', { duration: 2500 });
      return;
    }

    const cleaned: Match[] = matches.map(m => {
      const reset: Match = {
        ...m,
        state: MatchState.PROGRAMADO,
        court: '',
        games: [],
        gamesDetailed: [],
        playerIdWinner: null,
        player1GamesWon: 0,
        player1GamesLost: 0,
        player1PointsWon: 0,
        player1PointsLost: 0,
        player2GamesWon: 0,
        player2GamesLost: 0,
        player2PointsWon: 0,
        player2PointsLost: 0,
      };
      // Quitar campos derivados de ControlMatch que NO van a Firestore.
      delete (reset as any).priority;
      delete (reset as any).manualOrder;
      delete (reset as any).categoryColor;
      delete (reset as any).progressPercent;
      delete (reset as any).estimatedMinutesLeft;
      delete (reset as any).playerAvailable;
      delete (reset as any).player1;
      delete (reset as any).player2;
      delete (reset as any).globalPosition;
      return reset;
    });

    // Limpiar el estado local del referee (localStorage) por código de partido.
    matches.forEach(m => {
      if (m.code) {
        try { localStorage.removeItem(`referee_state_${m.code}`); } catch { /* noop */ }
      }
    });

    forkJoin(cleaned.map(c => this.service.updateMatch(c))).subscribe({
      next: () => {
        this.snackBar.open(`Se reiniciaron ${cleaned.length} partidos`, 'Cerrar', { duration: 3000 });
      },
      error: (err) => {
        console.error('Error reiniciando partidos:', err);
        this.snackBar.open('Error al reiniciar partidos. Revisa la consola.', 'Cerrar', { duration: 4000 });
      },
    });
  }

  toggleFinalizados(): void {
    this.showFinalizados = !this.showFinalizados;
  }

  // --- Procesamiento principal ---

  private lastMatches: MatchExtended[] = [];

  private processMatches(matches: MatchExtended[]): void {
    this.lastMatches = matches;
    this.playerStats = computePlayerStats(matches);

    // 1. Split por estado SIN filtrar — necesitamos la visión global
    const { enCancha, pendientes, finalizados } = splitByState(matches);

    // 2. Detectar resets (partidos que estaban en cancha y volvieron a PROGRAMADO).
    //    Solo después de tener el snapshot inicial del orden de Firestore,
    //    para evitar falsos positivos al cargar la página.
    if (this.hasInitialOrder && this.previousInCourtIds.size > 0) {
      const resetMatches = pendientes.filter(m => m.id && this.previousInCourtIds.has(m.id));
      if (resetMatches.length > 0) {
        this.parkMatchesAtBottom(resetMatches);
        // saveControlOrder dispara un nuevo ciclo via controlOrderSub.
        // No continuamos este ciclo porque vendrá uno fresh.
        this.previousInCourtIds = new Set(
          enCancha.map(m => m.id!).filter(Boolean) as string[]
        );
        return;
      }
    }

    // 3. Ordenar la lista global de pendientes por prioridad (incluye orden manual)
    const globalSorted = sortByPriority(pendientes, this.currentManualOrder, {
      playerStats: this.playerStats,
      lastMatches: matches,
      now: Date.now(),
    });

    // 4. Asignar posición global a cada partido (1-indexed)
    globalSorted.forEach((m, i) => (m.globalPosition = i + 1));
    this.globalPendientesSorted = globalSorted;

    // 5. Calcular cuántos partidos son "Siguientes" según canchas libres
    this.matchesEnCancha = enCancha;
    this.updateFreeCourts();
    const siguientesCount = Math.max(3, this.freeCourts + 1);
    const globalSiguientes = globalSorted.slice(0, siguientesCount);
    const globalEspera = globalSorted.slice(siguientesCount);

    // 6. Aplicar filtro de categoría (y opcionalmente de grupo) como vista
    //    (no recalcula posiciones). El filtro de grupo solo aplica cuando hay
    //    una única categoría seleccionada.
    this.computeAvailableGroups(matches);
    const cat = this.selectedCategories;
    const group = this.selectedGroup;
    const matchesFilter = (arr: ControlMatch[]) => {
      let out = cat.size === 0 ? arr : arr.filter(m => m.category && cat.has(m.category));
      if (group) out = out.filter(m => m.groupName === group);
      return out;
    };

    this.matchesSiguientes = matchesFilter(globalSiguientes);
    this.matchesEnEspera = matchesFilter(globalEspera);
    this.matchesEnCancha = matchesFilter(enCancha);
    this.matchesFinalizados = matchesFilter(
      [...finalizados].sort((a, b) => {
        const aMs = (a.matchEndedAt as any)?.toMillis?.() || 0;
        const bMs = (b.matchEndedAt as any)?.toMillis?.() || 0;
        return bMs - aMs;
      })
    );

    // 7. Refrescar registro de "en cancha" para el próximo ciclo
    this.previousInCourtIds = new Set(
      enCancha.map(m => m.id!).filter(Boolean) as string[]
    );
  }

  /**
   * Asigna un manualOrder >= PARK_BOTTOM_THRESHOLD a los partidos
   * indicados, garantizando que aparezcan al final de la cola global.
   * Usado al detectar resets.
   */
  private parkMatchesAtBottom(matches: ControlMatch[]): void {
    let maxParked = PARK_BOTTOM_THRESHOLD;
    for (const id in this.currentManualOrder) {
      const v = this.currentManualOrder[id];
      if (v >= PARK_BOTTOM_THRESHOLD && v > maxParked) maxParked = v;
    }

    const update: Record<string, number> = { ...this.currentManualOrder };
    for (const m of matches) {
      if (m.id) {
        maxParked += 1;
        update[m.id] = maxParked;
      }
    }
    this.service.saveControlOrder(update);
  }

  private reprocessCurrentMatches(): void {
    if (this.lastMatches.length > 0) {
      this.processMatches(this.lastMatches);
    }
  }

  // --- Config de canchas ---

  private loadCourtsConfig(): number {
    return this.service.getTotalCourts();
  }

  private saveCourtsConfig(): void {
    this.service.setTotalCourts(this.totalCourts);
  }

  private updateFreeCourts(): void {
    this.freeCourts = Math.max(0, this.totalCourts - this.matchesEnCancha.length);
  }
}
